/**
 * OperatorsTab — Phase 24 / Task 24.3 (DEC-094).
 *
 * Lists the hauler's operators (platform-catalog + hauler-private customers).
 * Add flow: search the platform catalog → confirm (abbreviation + billing), or
 * "Add as customer" for operators not in the catalog. Managers can click a row
 * to edit abbreviation + billing; plain dispatchers get a read-only list.
 *
 * OPEN-201 (c) — under the list, "Operators of your wells not on this list": the catalog
 * operators of the hauler's pickup and tank-battery wells that are not on it (Corbin: never
 * added on their own). Managers get a one-click Add (Paelode's code is used); when Paelode
 * has no code for the operator, or another customer already uses it, the add dialog opens for
 * that operator so a code can be typed. An operator the hauler switched off is switched back on.
 */
import { useExpressCustomerState } from '../../lib/express-customer';
import * as React from 'react';
import { toast } from 'sonner';
import { ApiError, apiErrorText } from '@lodex-pro/web-shared';
import {
  useHaulerOperators,
  useDeleteHaulerOperator,
  usePlatformOperatorSearch,
  useMissingOperators,
  useCreateHaulerOperator,
  useUpdateHaulerOperator,
  type HaulerOperator,
  type MissingOperator,
  type PlatformOperatorResult,
} from './hooks';
import { CatalogSearchModal } from './CatalogSearchModal';
import { OperatorLoadEmailsDetail } from '../load-emails/OperatorLoadEmailsDetail';
import { OperatorFormModal, type OperatorFormMode } from './OperatorFormModal';
import { useUrlDetail, useUrlParam } from '../../lib/url-state';
import {
  ActiveBadge,
  AddButton,
  Pill,
  SectionHeader,
  rowStyle,
  searchInputStyle,
  subText,
} from './ui-bits';
import { formatNumber } from '@lodex-pro/shared/number-format';

function usePlatformSearch(q: string): {
  items: readonly PlatformOperatorResult[];
  isFetching: boolean;
} {
  const { data, isFetching } = usePlatformOperatorSearch(q);
  return { items: data?.operators ?? [], isFetching };
}

/** Row-level delete control — TENANT_ADMIN only; isolated so the hook is per-row. */
function DeactivateOperatorButton({ op }: { op: HaulerOperator }): React.JSX.Element {
  const del = useDeleteHaulerOperator(op.id);
  return (
    <button
      type="button"
      disabled={del.isPending}
      onClick={(e): void => {
        e.stopPropagation();
        del.mutate(undefined, {
          onSuccess: () => toast.success('Operator deactivated'),
          onError: () => toast.error('Could not deactivate operator'),
        });
      }}
      style={{
        fontSize: '12px',
        color: 'var(--color-danger)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      Deactivate
    </button>
  );
}

/** OPEN-201 (c) — the catalog row the add dialog shows, built from a missing operator. */
function asPlatformOperator(m: MissingOperator): PlatformOperatorResult {
  return {
    id: m.operatorId,
    name: m.officialName,
    displayName: m.displayName,
    abbreviation: m.platformAbbreviation,
    state: m.state,
    regulatorOperatorId: m.regulatorOperatorId,
    isProducer: m.isProducer,
    isDisposal: m.isDisposal,
  };
}

/** Which code refusal a one-click Add hit (the API's field reasons), or null for another error. */
function codeRefusal(err: unknown): 'none' | 'taken' | null {
  if (!(err instanceof ApiError)) return null;
  if (err.fields.some((f) => f.reason === 'required_no_platform_abbreviation')) return 'none';
  if (err.fields.some((f) => f.reason === 'platform_abbreviation_taken')) return 'taken';
  return null;
}

/** One row of "Operators of your wells not on this list"; isolated so the hooks are per-row. */
function MissingOperatorRow({
  op,
  canAdd,
  onNeedsCode,
}: {
  op: MissingOperator;
  canAdd: boolean;
  onNeedsCode: (op: MissingOperator, taken: boolean) => void;
}): React.JSX.Element {
  // Both refresh the operators list and this section (MISSING_OPERATORS_KEY sits under it).
  const create = useCreateHaulerOperator(() =>
    toast.success(`Added ${op.name} to your operators.`),
  );
  const reactivate = useUpdateHaulerOperator(op.inactiveHaulerOperatorId ?? '');
  const pending = create.isPending || reactivate.isPending;
  const add = (): void => {
    if (op.inactiveHaulerOperatorId !== null) {
      reactivate.mutate(
        { isActive: true },
        {
          onSuccess: () => toast.success(`Turned ${op.name} back on in your operators.`),
          onError: (err) => toast.error(apiErrorText(err, `Could not add ${op.name}.`)),
        },
      );
      return;
    }
    create.mutate(
      { isPlatformOperator: true, operatorId: op.operatorId },
      {
        onError: (err) => {
          const refusal = codeRefusal(err);
          if (refusal !== null) onNeedsCode(op, refusal === 'taken');
          else toast.error(apiErrorText(err, `Could not add ${op.name}.`));
        },
      },
    );
  };
  return (
    <div style={rowStyle} data-testid="missing-operator-row">
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>{op.name}</span>
        <div style={subText}>
          {formatNumber(op.wellCount)} {op.wellCount === 1 ? 'well' : 'wells'}
        </div>
      </div>
      {canAdd && (
        <AddButton
          label={op.inactiveHaulerOperatorId !== null ? 'Turn back on' : 'Add'}
          onClick={add}
          disabled={pending}
        />
      )}
    </div>
  );
}

export function OperatorsTab({
  canManage,
  isTenantAdmin,
  focusOperatorId = null,
}: {
  canManage: boolean;
  isTenantAdmin: boolean;
  /** `?operatorId=` deep link — opens this customer's pricing form on arrival. */
  focusOperatorId?: string | null;
}): React.JSX.Element {
  const { data, isLoading, isError } = useHaulerOperators();
  // §58 PR 4 — the hauler's one customer when it is on EXPRESS.
  const { express, pending: expressPending } = useExpressCustomerState();
  // OPEN-191 — the filter and the open load-emails pane live in the URL (`?operator=<id>`), so
  // browser Back closes the pane and a refresh reopens it. An id not in the list shows the list.
  const [filter, setFilter] = useUrlParam('operatorsQ', '', []);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [formOpen, setFormOpen] = React.useState(false);
  const [formMode, setFormMode] = React.useState<OperatorFormMode>('platform');
  const [picked, setPicked] = React.useState<PlatformOperatorResult | null>(null);
  const [editing, setEditing] = React.useState<HaulerOperator | null>(null);
  // Phase 17 / Task 17.3 — the customer whose load-email contacts + settings pane is open.
  const [emailsForId, setEmailsForId] = useUrlDetail('operator');
  // OPEN-201 (c) — operators of the hauler's wells that are not on the list.
  const { data: missingData } = useMissingOperators();
  const missing = missingData?.operators ?? [];
  // A one-click Add was refused for want of a code: the add dialog opens already asking for one.
  const [codeTaken, setCodeTaken] = React.useState(false);
  const canAdd = canManage && express === null && !expressPending;

  // Memoised so the deep-link effect below depends on a stable reference — a fresh
  // [] every render would re-run it on every render.
  const operators = React.useMemo(() => data?.haulerOperators ?? [], [data]);
  const emailsFor =
    emailsForId === null ? null : (operators.find((o) => o.id === emailsForId) ?? null);
  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? operators.filter(
        (o) =>
          o.resolvedName.toLowerCase().includes(needle) ||
          o.ticketAbbreviation.toLowerCase().includes(needle) ||
          (o.billingContactName ?? '').toLowerCase().includes(needle),
      )
    : operators;

  const openEdit = (op: HaulerOperator): void => {
    if (!canManage) return;
    setCodeTaken(false);
    setEditing(op);
    setPicked(null);
    setFormMode('edit');
    setFormOpen(true);
  };

  // Arriving from the InReview no-rate banner: open the named customer's form as
  // soon as the list resolves, so the reviewer lands on the rate field itself.
  // Guarded by a ref because the effect must not reopen the modal every render —
  // that would trap the user in a dialog they just dismissed. A reviewer without
  // MANAGE_HAULER_CATALOG simply sees the list; the server is authoritative either
  // way, and openEdit would refuse the write anyway.
  const focusHandledRef = React.useRef(false);
  React.useEffect(() => {
    if (focusOperatorId === null || focusHandledRef.current || !canManage) return;
    const match = operators.find((o) => o.id === focusOperatorId);
    if (match === undefined) return;
    focusHandledRef.current = true;
    setEditing(match);
    setPicked(null);
    setFormMode('edit');
    setFormOpen(true);
  }, [focusOperatorId, operators, canManage]);

  if (emailsFor !== null) {
    return (
      <OperatorLoadEmailsDetail
        customer={emailsFor}
        canManage={canManage}
        onClose={(): void => setEmailsForId(null)}
      />
    );
  }

  return (
    <div>
      <SectionHeader
        title="Operators"
        count={operators.length}
        action={
          // §58 PR 4 — on EXPRESS the one customer is set by Paelode; nothing to add here.
          canAdd ? (
            <AddButton label="Add Operator" onClick={(): void => setSearchOpen(true)} />
          ) : null
        }
      />
      {express !== null && (
        <p
          data-testid="express-customer-note"
          style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: '0 0 8px' }}
        >
          {`Your current plan covers one customer: ${express.name}. Contact Paelode support to add others.`}
        </p>
      )}
      <input
        value={filter}
        onChange={(e): void => setFilter(e.target.value)}
        placeholder="Filter operators…"
        style={searchInputStyle}
        aria-label="Filter operators"
      />

      {isLoading && <p style={subText}>Loading…</p>}
      {isError && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load operators.</p>
      )}
      {!isLoading && visible.length === 0 && <p style={subText}>No operators yet.</p>}

      <div>
        {visible.map((op) => (
          <div
            key={op.id}
            style={{
              ...rowStyle,
              cursor: canManage ? 'pointer' : 'default',
              opacity: op.isActive ? 1 : 0.55,
            }}
            onClick={(): void => openEdit(op)}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 600 }}>{op.resolvedName}</span>
                {/* 0202: the code its tickets carry (Paelode's for a catalog operator). */}
                <Pill label={op.ticketAbbreviation} tone="accent" />
                {/* DEC-109 — "Platform" → "lodeX Pro" for label consistency with the
                    Wells tab "lodeX Pro Catalog" badge (the operator comes from the
                    lodeX Pro catalog vs a hauler-private "Customer"). */}
                <Pill label={op.isPlatformOperator ? 'lodeX Pro' : 'Customer'} tone="neutral" />
              </div>
              <div style={subText}>
                {op.billingContactName ?? 'No billing contact'}
                {op.catalogState != null ? ` · ${op.catalogState}` : ''}
                {op.regulatorOperatorId != null ? ` · OCC ${op.regulatorOperatorId}` : ''}
                {/* DEC-198 — the rate is visible on the row, because a customer with
                    no rate is the thing that silently blocks its tickets from billing. */}
                {op.perBblPriceCents != null &&
                  ` · $${formatNumber(op.perBblPriceCents / 100, { decimals: 2 })}/bbl`}
                {op.hourlyPriceCents != null &&
                  ` · $${formatNumber(op.hourlyPriceCents / 100, { decimals: 2 })}/hr`}
              </div>
            </div>
            <button
              type="button"
              aria-label={`Haul Confirmation emails for ${op.resolvedName}`}
              onClick={(e): void => {
                e.stopPropagation();
                setEmailsForId(op.id);
              }}
              style={{
                minHeight: '56px',
                padding: '0 14px',
                fontSize: '14px',
                color: 'var(--color-accent-blue)',
                background: 'none',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: 'var(--radius-pill)',
                cursor: 'pointer',
              }}
            >
              Haul Confirmation emails
            </button>
            <ActiveBadge active={op.isActive} />
            {isTenantAdmin && op.isActive && <DeactivateOperatorButton op={op} />}
          </div>
        ))}
      </div>

      {/* OPEN-201 (c) — hidden when every operator of the hauler's wells is on the list. */}
      {missing.length > 0 && (
        <div style={{ marginTop: '20px' }} data-testid="missing-operators">
          <SectionHeader title="Operators of your wells not on this list" count={missing.length} />
          {missing.map((m) => (
            <MissingOperatorRow
              key={m.operatorId}
              op={m}
              canAdd={canAdd}
              onNeedsCode={(op, taken): void => {
                setPicked(asPlatformOperator(op));
                setEditing(null);
                setCodeTaken(taken);
                setFormMode('platform');
                setFormOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Step 1 — search the platform catalog (or add as customer). */}
      <CatalogSearchModal<PlatformOperatorResult>
        open={searchOpen}
        onOpenChange={setSearchOpen}
        title="Add operator"
        placeholder="Search platform operators by name…"
        useSearch={usePlatformSearch}
        itemKey={(o): string => o.id}
        renderItem={(o): React.ReactNode => (
          <span>
            <span style={{ fontWeight: 600 }}>{o.displayName ?? o.name}</span>
            <span style={subText}>
              {o.displayName != null && o.displayName !== o.name ? ` · ${o.name}` : ''}
              {o.state != null ? ` · ${o.state}` : ''}
              {o.regulatorOperatorId != null ? ` · OCC ${o.regulatorOperatorId}` : ''}
            </span>
          </span>
        )}
        onPick={(o): void => {
          setSearchOpen(false);
          setCodeTaken(false);
          setPicked(o);
          setEditing(null);
          setFormMode('platform');
          setFormOpen(true);
        }}
        // §58 — on EXPRESS no customer can be added (the API refuses too).
        footer={
          express !== null || expressPending ? undefined : (
            <button
              type="button"
              onClick={(): void => {
                setSearchOpen(false);
                setPicked(null);
                setEditing(null);
                setFormMode('customer');
                setFormOpen(true);
              }}
              style={{
                fontSize: '13px',
                color: 'var(--color-accent-blue)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              Not in catalog? Add as customer
            </button>
          )
        }
      />

      {/* Step 2 — confirm/create or edit. */}
      <OperatorFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        platformOperator={picked}
        editing={editing}
        initialCodeTaken={codeTaken}
      />
    </div>
  );
}
